import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import type { FC } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@ui/dialog.tsx';
import { Button } from '@ui/button.tsx';
import { Input } from '@ui/input.tsx';
import { Checkbox } from '@ui/checkbox.tsx';
import { Label } from '@ui/label.tsx';
import { toast } from 'sonner';
import type { ReferenceReferenceReadModel } from '@r/api/generated/client.schemas.ts';
import {
  type BoosterReferenceTier,
  createDefaultReferenceTier,
  normalizeCustomTiers,
  validateReferenceTiers
} from './booster-reference-tiers.ts';

interface BoosterReferenceCoefficientDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  reference: ReferenceReferenceReadModel | null;
  globalCoefficient: number;
  initialTiers: BoosterReferenceTier[];
  onSave: (tiers: BoosterReferenceTier[] | undefined) => void;
}

type EditableBoosterReferenceTier = BoosterReferenceTier & { rowKey: string };

function createRowKey(): string {
  return crypto.randomUUID();
}

function toEditableTiers(tiers: BoosterReferenceTier[]): EditableBoosterReferenceTier[] {
  return tiers.map((tier) => ({ ...tier, rowKey: createRowKey() }));
}

function stripRowKeys(tiers: EditableBoosterReferenceTier[]): BoosterReferenceTier[] {
  return tiers.map(({ rowKey: _rowKey, ...tier }) => tier);
}

function createEmptyTier(globalCoefficient: number): EditableBoosterReferenceTier {
  return {
    minQuantity: 1,
    maxQuantity: null,
    coefficient: globalCoefficient,
    rowKey: createRowKey()
  };
}

/** Modale de configuration des paliers de coefficient pour une référence. */
export const BoosterReferenceCoefficientDialog: FC<BoosterReferenceCoefficientDialogProps> = ({
  isOpen,
  onOpenChange,
  reference,
  globalCoefficient,
  initialTiers,
  onSave
}) => {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState<EditableBoosterReferenceTier[]>(() =>
    toEditableTiers(
      initialTiers.length > 0 ? initialTiers : [createDefaultReferenceTier(globalCoefficient)]
    )
  );

  const updateTier = (index: number, patch: Partial<BoosterReferenceTier>) => {
    setTiers((previous) =>
      previous.map((tier, tierIndex) => (tierIndex === index ? { ...tier, ...patch } : tier))
    );
  };

  const handleAddTier = () => {
    setTiers((previous) => [...previous, createEmptyTier(globalCoefficient)]);
  };

  const handleRemoveTier = (index: number) => {
    setTiers((previous) => previous.filter((_, tierIndex) => tierIndex !== index));
  };

  const handleSave = () => {
    const validationError = validateReferenceTiers(stripRowKeys(tiers), t);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    onSave(normalizeCustomTiers(stripRowKeys(tiers), globalCoefficient));
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden p-0"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="overflow-y-auto flex-1 px-8 pt-8 pb-6 min-h-0 scrollable sm:px-10 sm:pt-10">
          <DialogHeader>
            <DialogTitle>{t('boosters.create.references.tiers.dialog.title')}</DialogTitle>
            <DialogDescription>
              {reference
                ? t('boosters.create.references.tiers.dialog.description', {
                    reference: reference.uniqueCode
                  })
                : t('boosters.create.references.tiers.dialog.description.generic')}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('boosters.create.references.tiers.dialog.help', {
                coefficient: globalCoefficient
              })}
            </p>

            {tiers.map((tier, index) => (
              <div
                key={tier.rowKey}
                className="grid gap-3 rounded-lg border border-border/60 p-4 sm:grid-cols-[1fr_1fr_1fr_auto]"
              >
                <div className="space-y-1.5">
                  <Label htmlFor={`tier-min-${index}`}>
                    {t('boosters.create.references.tiers.min.label')}
                  </Label>
                  <Input
                    id={`tier-min-${index}`}
                    type="number"
                    min={0}
                    step={1}
                    value={tier.minQuantity}
                    onChange={(event) =>
                      updateTier(index, { minQuantity: Number(event.target.value) || 0 })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor={`tier-max-${index}`}>
                    {t('boosters.create.references.tiers.max.label')}
                  </Label>
                  <Input
                    id={`tier-max-${index}`}
                    type="number"
                    min={0}
                    step={1}
                    disabled={tier.maxQuantity == null}
                    value={tier.maxQuantity ?? ''}
                    placeholder={t('boosters.create.references.tiers.max.unbounded')}
                    onChange={(event) =>
                      updateTier(index, {
                        maxQuantity: event.target.value === '' ? null : Number(event.target.value)
                      })
                    }
                  />
                  <div className="flex gap-2 items-center pt-1">
                    <Checkbox
                      id={`tier-unbounded-${index}`}
                      checked={tier.maxQuantity == null}
                      onCheckedChange={(checked) =>
                        updateTier(index, { maxQuantity: checked ? null : tier.minQuantity })
                      }
                    />
                    <Label htmlFor={`tier-unbounded-${index}`} className="text-xs font-normal">
                      {t('boosters.create.references.tiers.max.unbounded.checkbox')}
                    </Label>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor={`tier-coefficient-${index}`}>
                    {t('boosters.create.references.tiers.coefficient.label')}
                  </Label>
                  <Input
                    id={`tier-coefficient-${index}`}
                    type="number"
                    min={0}
                    step={1}
                    value={tier.coefficient}
                    onChange={(event) =>
                      updateTier(index, { coefficient: Number(event.target.value) || 0 })
                    }
                  />
                </div>

                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleRemoveTier(index)}
                    disabled={tiers.length <= 1}
                    aria-label={t('boosters.create.references.tiers.remove.aria')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}

            <Button type="button" variant="outline" rounded="full" onClick={handleAddTier}>
              <Plus className="mr-2 w-4 h-4" />
              {t('boosters.create.references.tiers.add')}
            </Button>
          </div>
        </div>

        <DialogFooter className="gap-2 px-8 py-4 border-t shrink-0 border-border/60 bg-background sm:gap-0 sm:px-10">
          <Button
            type="button"
            variant="outline"
            rounded="full"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button type="button" rounded="full" onClick={handleSave}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
